import * as React from 'react';
import { brandColors, companyLogo, companyConfig } from '../config/colors';
import EmailFooterV2 from '../components/FooterV2';

interface NewUserRegistrationEmailProps {
  // Dados do usuário
  user_id?: string;
  email?: string;
  user_name?: string;
  registration_date?: string;
  
  // Dados da organização
  org_name?: string;
  industry?: string;
  
  // Dados da assinatura/plano
  subscription_id?: string;
  plan_name?: string;
  plan_type?: string;
  subscription_start_date?: string;
  subscription_end_date?: string;
  
  // Links de ativação e configuração
  activation_link?: string;
  dashboard_link?: string;
  support_link?: string;
  
  // Configurações
  welcome_message?: string;
  next_steps?: string[];
  
  // Dados do criador/administrador
  created_by?: string;
  created_by_name?: string;
  created_by_email?: string;
}

export const NewUserRegistrationEmail: React.FC<NewUserRegistrationEmailProps> = ({
  user_id = 'USR-12345',
  email = 'joao.silva@empresa.com.br',
  user_name = 'João Silva',
  registration_date = '2025-01-15T10:30:00Z',
  
  org_name = 'Empresa XYZ Ltda',
  industry = 'Tecnologia',
  
  subscription_id = 'SUB-789',
  plan_name = 'Plano Profissional',
  plan_type = 'premium',
  subscription_start_date = '2025-01-15',
  subscription_end_date = '2025-12-31',
  
  activation_link = 'https://trivium.com.br/activate/abc123',
  dashboard_link = 'https://trivium.com.br/dashboard',
  support_link = 'https://trivium.com.br/support',
  
  welcome_message = 'Seja bem-vindo à nossa plataforma de gestão educacional!',
  next_steps = [
    'Ative sua conta clicando no link de ativação',
    'Configure seu perfil e preferências',
    'Explore os recursos disponíveis em seu plano',
    'Convide sua equipe para colaborar'
  ],
  
  created_by = 'ADMIN-001',
  created_by_name = 'Maria Administradora',
  created_by_email = 'admin@empresaxyz.com.br',
}) => {
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getPlanIcon = (type: string) => {
    switch (type.toLowerCase()) {
      case 'basic':
        return '📦';
      case 'premium':
        return '⭐';
      case 'enterprise':
        return '🏢';
      default:
        return '📋';
    }
  };

  const getPlanColor = (type: string) => {
    switch (type.toLowerCase()) {
      case 'basic':
        return '#94a3b8';
      case 'premium':
        return '#c8e600';
      case 'enterprise':
        return '#8b5cf6';
      default:
        return '#c8e600';
    }
  };

  return (
    <div style={containerStyle}>
      {/* Header */}
      <div style={headerStyle}>
        <img 
          src={companyLogo}
          alt={companyConfig.name}
          style={logoImageStyle}
        />
      </div>
      
      {/* Hero Section */}
      <div style={heroSectionStyle}>
        <div style={heroContentStyle}>
          {/* Welcome Badge */}
          <div style={{
            ...statusBadgeStyle,
            backgroundColor: getPlanColor(plan_type),
          }}>
            <span>🎉 Conta criada com sucesso!</span>
          </div>
          
          <h1 style={heroTitleStyle}>
            Bem-vindo(a), {user_name}! 👋
          </h1>
          
          <p style={heroSubtitleStyle}>
            Sua conta foi criada na plataforma {companyConfig.name}.<br />
            Ative sua conta para começar a usar todos os recursos.
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div style={mainContentStyle}>
        
        {/* Account Summary */}
        <div style={summaryCardStyle}>
          <h2 style={summaryTitleStyle}>Detalhes da Conta</h2>
          <div style={summaryContentStyle}>
            <div style={summaryItemStyle}>
              <strong>Nome:</strong> {user_name}
            </div>
            <div style={summaryItemStyle}>
              <strong>Email:</strong> {email}
            </div>
            <div style={summaryItemStyle}>
              <strong>Plano:</strong> {getPlanIcon(plan_type)} {plan_name}
            </div>
            <div style={summaryItemStyle}>
              <strong>Organização:</strong> {org_name}
            </div>
            <div style={summaryItemStyle}>
              <strong>Status:</strong> <span style={statusPendingStyle}>Aguardando Ativação</span>
            </div>
          </div>
        </div>

        {/* Main CTA */}
        <div style={ctaSectionStyle}>
          <h2 style={ctaTitleStyle}>Para começar a usar sua conta:</h2>
          <a href={activation_link} style={mainCtaButtonStyle}>
            🔐 Ativar Minha Conta
          </a>
          <p style={ctaSubtextStyle}>
            O link de ativação expira em 24 horas
          </p>
        </div>

        {/* Quick Steps */}
        <div style={stepsCardStyle}>
          <h3 style={stepsHeaderStyle}>Próximos passos após a ativação:</h3>
          <ol style={stepsListStyle}>
            {next_steps.map((step, index) => (
              <li key={index} style={stepItemCleanStyle}>{step}</li>
            ))}
          </ol>
        </div>

        {/* Additional Actions */}
        <div style={additionalActionsStyle}>
          <p style={additionalTextStyle}>Também disponível:</p>
          <a href={dashboard_link} style={secondaryLinkStyle}>
            📊 Acessar Dashboard
          </a>
        </div>

      </div>
      
      {/* Footer */}
      <EmailFooterV2 variant="clean" showContactInfo={true} />
    </div>
  );
};

// Estilos (baseados no ClassConfirmationEmailV3)
const containerStyle = {
  fontFamily: "Arial, sans-serif",
  maxWidth: '600px',
  margin: '0 auto',
  backgroundColor: '#f8fafc',
  lineHeight: '1.5',
};

const headerStyle = {
  backgroundColor: brandColors.header,
  padding: '2px',
  textAlign: 'center' as const,
  borderTopLeftRadius: '12px',
  borderTopRightRadius: '12px',
};

const logoImageStyle = {
  maxWidth: '140px',
  height: 'auto',
  display: 'block',
  margin: '0 auto',
};

const heroSectionStyle = {
  backgroundColor: '#f8f9fa',
  padding: '40px 24px',
  textAlign: 'center' as const,
  color: '#2d3748',
};

const heroContentStyle = {
  maxWidth: '480px',
  margin: '0 auto',
};

const statusBadgeStyle = {
  display: 'inline-block',
  padding: '6px 12px',
  borderRadius: '12px',
  fontSize: '12px',
  fontWeight: '700',
  color: '#2d3748',
  marginBottom: '20px',
  backgroundColor: '#10b981',
};

const heroTitleStyle = {
  fontSize: '24px',
  fontWeight: '600',
  margin: '0 0 12px 0',
  color: '#2d3748',
};

const heroSubtitleStyle = {
  fontSize: '14px',
  margin: '0',
  opacity: '0.9',
  color: '#4a5568',
  lineHeight: '1.4',
};

const planNameBoldStyle = {
  fontSize: '16px',
  fontWeight: '700',
  color: '#2d3748',
};

const mainContentStyle = {
  padding: '24px',
  backgroundColor: brandColors.white,
};

const infoTableStyle = {
  width: '100%',
  marginBottom: '24px',
  borderCollapse: 'collapse' as const,
};

const infoCellStyle = {
  width: '50%',
  padding: '0 8px',
  verticalAlign: 'top' as const,
};

const infoBlockTableStyle = {
  width: '100%',
  backgroundColor: '#f8fafc',
  borderRadius: '8px',
  border: '1px solid #e2e8f0',
};

const infoBlockCellStyle = {
  padding: '16px',
  verticalAlign: 'top' as const,
};

const infoBadgeStyle = {
  display: 'inline-block',
  padding: '4px 8px',
  backgroundColor: brandColors.primary,
  color: brandColors.white,
  borderRadius: '6px',
  fontSize: '11px',
  fontWeight: '500',
  marginBottom: '8px',
};

const infoValueStyle = {
  fontSize: '16px',
  fontWeight: '600',
  color: brandColors.text,
  marginBottom: '4px',
};

// Clean Layout Styles
const summaryCardStyle = {
  padding: '20px',
  backgroundColor: '#f8fafc',
  borderRadius: '8px',
  border: '1px solid #e2e8f0',
  marginBottom: '24px',
};

const summaryTitleStyle = {
  fontSize: '18px',
  fontWeight: '600',
  color: brandColors.text,
  margin: '0 0 16px 0',
};

const summaryContentStyle = {
  display: 'flex',
  flexDirection: 'column' as const,
  gap: '8px',
};

const summaryItemStyle = {
  fontSize: '14px',
  color: brandColors.text,
  lineHeight: '1.5',
};

const statusPendingStyle = {
  color: '#d97706',
  fontWeight: '600',
};

const ctaSectionStyle = {
  textAlign: 'center' as const,
  margin: '32px 0',
  padding: '24px',
  backgroundColor: '#ffffff',
  borderRadius: '8px',
  border: '1px solid #e2e8f0',
};

const ctaTitleStyle = {
  fontSize: '16px',
  fontWeight: '600',
  color: brandColors.text,
  margin: '0 0 20px 0',
};

const mainCtaButtonStyle = {
  display: 'inline-block',
  padding: '14px 28px',
  backgroundColor: brandColors.primary,
  color: brandColors.white,
  textDecoration: 'none',
  borderRadius: '8px',
  fontSize: '16px',
  fontWeight: '600',
  margin: '0 0 12px 0',
};

const ctaSubtextStyle = {
  fontSize: '13px',
  color: brandColors.secondaryText,
  margin: '0',
};

const stepsCardStyle = {
  padding: '20px',
  backgroundColor: '#f8fafc',
  borderRadius: '8px',
  border: '1px solid #e2e8f0',
  marginBottom: '24px',
};

const stepsHeaderStyle = {
  fontSize: '16px',
  fontWeight: '600',
  color: brandColors.text,
  margin: '0 0 12px 0',
};

const stepsListStyle = {
  margin: '0',
  paddingLeft: '20px',
  color: brandColors.text,
};

const stepItemCleanStyle = {
  fontSize: '14px',
  lineHeight: '1.6',
  marginBottom: '8px',
};

const additionalActionsStyle = {
  textAlign: 'center' as const,
  marginBottom: '24px',
};

const additionalTextStyle = {
  fontSize: '14px',
  color: brandColors.secondaryText,
  margin: '0 0 8px 0',
};

const secondaryLinkStyle = {
  display: 'inline-block',
  padding: '10px 20px',
  color: brandColors.primary,
  textDecoration: 'none',
  border: `1px solid ${brandColors.primary}`,
  borderRadius: '6px',
  fontSize: '14px',
  fontWeight: '500',
};

export default NewUserRegistrationEmail;
